import React, { useState, useEffect } from 'react';
import { Box, Typography, TextField, Button, Grid, Card, CardContent, Tabs, Tab, Switch, FormControlLabel, Avatar, List, ListItem, ListItemText, ListItemSecondaryAction } from '@mui/material';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import api from '../services/api';
import SettingsIcon from '@mui/icons-material/Settings';
import PersonIcon from '@mui/icons-material/Person';
import AppsIcon from '@mui/icons-material/Apps';
import WallpaperIcon from '@mui/icons-material/Wallpaper';
import { designTokens } from '../theme/designTokens';
import { decryptPrivateKey, encryptPrivateKey } from '../utils/cryptoUtil';
import LockIcon from '@mui/icons-material/Lock';

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;
    return (
        <div role="tabpanel" hidden={value !== index} {...other}>
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
    );
}

const SettingsPage = () => {
    const { user, updateUser, setPrivateKey } = useAuth();
    const { showToast } = useToast();
    const [value, setValue] = useState(0);
    const [profileData, setProfileData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        currentPassword: '',
        password: '',
        photoUrl: ''
    });
    const [preferences, setPreferences] = useState({
        osBackground: '',
        language: 'fr'
    });
    const [enabledApps, setEnabledApps] = useState({
        cursus: true,
        courses: true,
        marketing: true,
        users: true,
        apprenants: true,
        monof: true,
        'block-evaluation': true,
        organizations: true,
        enrollment: true,
        learnerSpace: true,
        reports: true,
        questions: true
    });

    useEffect(() => {
        if (user) {
            setProfileData({
                firstName: user.firstName || '',
                lastName: user.lastName || '',
                email: user.email || '',
                currentPassword: '',
                password: '',
                photoUrl: user.preferences?.photoUrl || ''
            });
            setPreferences({
                osBackground: user.preferences?.osBackground || '',
                language: user.preferences?.language || 'fr'
            });
            // Update enabledApps from user preferences
            if (user.preferences?.enabledApps) {
                setEnabledApps({
                    cursus: user.preferences.enabledApps.cursus ?? true,
                    courses: user.preferences.enabledApps.courses ?? true,
                    marketing: user.preferences.enabledApps.marketing ?? true,
                    users: user.preferences.enabledApps.users ?? true,
                    apprenants: user.preferences.enabledApps.apprenants ?? true,
                    monof: user.preferences.enabledApps.monof ?? true,
                    'block-evaluation': user.preferences.enabledApps['block-evaluation'] ?? true,
                    organizations: user.preferences.enabledApps.organizations ?? true,
                    enrollment: user.preferences.enabledApps.enrollment ?? true,
                    learnerSpace: user.preferences.enabledApps.learnerSpace ?? true,
                    reports: user.preferences.enabledApps.reports ?? true,
                    questions: user.preferences.enabledApps.questions ?? true
                });
            }
        }
    }, [user]);

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setValue(newValue);
    };

    const handleProfileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setProfileData({ ...profileData, [e.target.name]: e.target.value });
    };

    const handlePreferenceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPreferences({ ...preferences, [e.target.name]: e.target.value });
    };

    const saveProfile = async () => {
        try {
            const updateData: any = {
                firstName: profileData.firstName,
                lastName: profileData.lastName,
                email: profileData.email,
                preferences: { ...user?.preferences, photoUrl: profileData.photoUrl }
            };

            if (profileData.password) {
                // ── E2EE key re-encryption on password change ──────────────────
                if (user?.encryptedPrivateKey && profileData.currentPassword) {
                    try {
                        // 1. Decrypt old private key using current password
                        const oldPrivateKey = await decryptPrivateKey(user.encryptedPrivateKey, profileData.currentPassword);
                        // 2. Re-encrypt with new password (encryptPrivateKey returns a Base64 string)
                        const newEncryptedPrivateKey = await encryptPrivateKey(oldPrivateKey, profileData.password);
                        updateData.encryptedPrivateKey = newEncryptedPrivateKey;
                        // 3. Update the in-memory private key for the session
                        setPrivateKey(oldPrivateKey);
                    } catch {
                        showToast('Mot de passe actuel incorrect — clé E2EE non migrée', 'error');
                        return;
                    }
                } else if (user?.encryptedPrivateKey && !profileData.currentPassword) {
                    showToast('Entrez votre mot de passe actuel pour migrer votre clé E2EE', 'error');
                    return;
                }
                updateData.password = profileData.password;
            }

            const res = await api.put('/api/users/profile', updateData);
            updateUser(res.data);
            setProfileData(p => ({ ...p, currentPassword: '', password: '' }));
            showToast('Profil mis à jour !', 'success');
        } catch (err) {
            console.error(err);
            showToast('Erreur lors de la mise à jour', 'error');
        }
    };

    const savePreferences = async () => {
        try {
            const res = await api.put('/api/users/profile', {
                preferences: { ...user?.preferences, ...preferences }
            });
            updateUser(res.data);
            showToast('Préférences sauvegardées !', 'success');
        } catch (err) {
            console.error(err);
            showToast('Erreur lors de la sauvegarde', 'error');
        }
    };

    const handleAppToggle = (app: string) => {
        setEnabledApps(prev => ({ ...prev, [app]: !prev[app as keyof typeof prev] }));
    };

    const saveAppSettings = async () => {
        try {
            const res = await api.put('/api/users/profile', {
                preferences: { ...user?.preferences, enabledApps }
            });
            console.log('saveAppSettings response:', res.data);
            console.log('enabledApps being saved:', enabledApps);
            updateUser(res.data);
            showToast('Applications mises à jour !', 'success');
        } catch (err) {
            console.error(err);
            showToast('Erreur lors de la mise à jour', 'error');
        }
    };

    return (
        <Box sx={{ p: 4, maxWidth: 1200, mx: 'auto' }}>
            <Box sx={designTokens.pageHeader}>
                <Typography variant="h4" sx={designTokens.pageTitle}>
                    <SettingsIcon fontSize="large" /> Paramètres
                </Typography>
            </Box>

            <Card sx={designTokens.glassCard}>
                <Tabs
                    value={value}
                    onChange={handleTabChange}
                    sx={{
                        borderBottom: 1,
                        borderColor: 'rgba(255,255,255,0.3)',
                        '& .MuiTab-root': {
                            fontWeight: 'bold',
                            fontSize: '1rem',
                            textTransform: 'none',
                            color: 'text.secondary',
                            '&.Mui-selected': { color: 'primary.main' }
                        }
                    }}
                >
                    <Tab icon={<PersonIcon />} label="Profil" iconPosition="start" />
                    <Tab icon={<WallpaperIcon />} label="Interface & OS" iconPosition="start" />
                    <Tab icon={<AppsIcon />} label="Applications" iconPosition="start" />
                </Tabs>

                <TabPanel value={value} index={0}>
                    <Grid container spacing={4}>
                        <Grid size={{ xs: 12, md: 4 }} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <Box sx={{
                                position: 'relative',
                                '&:hover .overlay': { opacity: 1 }
                            }}>
                                <Avatar
                                    src={profileData.photoUrl}
                                    sx={{
                                        width: 150,
                                        height: 150,
                                        mb: 2,
                                        fontSize: 60,
                                        boxShadow: '0 8px 20px rgba(0,0,0,0.2)',
                                        border: '4px solid white'
                                    }}
                                >
                                    {profileData.firstName[0]}
                                </Avatar>
                            </Box>
                            <TextField
                                label="URL Photo de profil"
                                name="photoUrl"
                                fullWidth
                                size="small"
                                value={profileData.photoUrl}
                                onChange={handleProfileChange}
                                sx={{ mb: 2, ...designTokens.input }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 8 }}>
                            <Grid container spacing={3}>
                                <Grid size={{ xs: 6 }}>
                                    <TextField label="Prénom" name="firstName" fullWidth value={profileData.firstName} onChange={handleProfileChange} sx={designTokens.input} />
                                </Grid>
                                <Grid size={{ xs: 6 }}>
                                    <TextField label="Nom" name="lastName" fullWidth value={profileData.lastName} onChange={handleProfileChange} sx={designTokens.input} />
                                </Grid>
                                <Grid size={{ xs: 12 }}>
                                    <TextField label="Email" name="email" fullWidth value={profileData.email} onChange={handleProfileChange} sx={designTokens.input} />
                                </Grid>
                                <Grid size={{ xs: 12 }}>
                                    {user?.encryptedPrivateKey && (
                                        <TextField
                                            label="Mot de passe actuel"
                                            name="currentPassword"
                                            type="password"
                                            fullWidth
                                            value={profileData.currentPassword}
                                            onChange={handleProfileChange}
                                            helperText="Requis pour migrer votre clé de chiffrement E2EE"
                                            sx={{ mb: 2, ...designTokens.input }}
                                            InputProps={{ startAdornment: <LockIcon sx={{ mr: 1, color: 'success.main', fontSize: 18 }} /> }}
                                        />
                                    )}
                                    <TextField
                                        label="Nouveau mot de passe"
                                        name="password"
                                        type="password"
                                        fullWidth
                                        value={profileData.password}
                                        onChange={handleProfileChange}
                                        helperText="Laisser vide pour ne pas changer"
                                        sx={designTokens.input}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12 }} sx={{ mt: 2 }}>
                                    <Button variant="contained" onClick={saveProfile} sx={designTokens.actionButton}>
                                        Sauvegarder le profil
                                    </Button>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </TabPanel>

                <TabPanel value={value} index={1}>
                    <Grid container spacing={4}>
                        <Grid size={{ xs: 12 }}>
                            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary' }}>Apparence</Typography>
                            <TextField
                                label="URL Arrière-plan (OS)"
                                name="osBackground"
                                fullWidth
                                value={preferences.osBackground}
                                onChange={handlePreferenceChange}
                                helperText="Lien vers une image ou code couleur CSS"
                                sx={{ mb: 4, ...designTokens.input }}
                            />

                            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary' }}>Langue</Typography>
                            <TextField
                                select
                                label="Langue"
                                name="language"
                                value={preferences.language}
                                onChange={handlePreferenceChange}
                                SelectProps={{ native: true }}
                                sx={designTokens.input}
                            >
                                <option value="fr">Français</option>
                                <option value="en">English</option>
                            </TextField>
                        </Grid>
                        <Grid size={{ xs: 12 }} sx={{ mt: 2 }}>
                            <Button variant="contained" onClick={savePreferences} sx={designTokens.actionButton}>
                                Sauvegarder les préférences
                            </Button>
                        </Grid>
                    </Grid>
                </TabPanel>

                <TabPanel value={value} index={2}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary', mb: 3 }}>
                        Gérer les applications visibles
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Activez ou désactivez les applications affichées sur le bureau.
                    </Typography>
                    <List>
                        {/* Define all possible apps with their metadata */}
                        {[
                            { id: 'cursus', name: 'Parcours', description: 'Gestion des parcours de formation' },
                            { id: 'courses', name: 'Cours', description: 'Liste et édition des cours' },
                            { id: 'marketing', name: 'Marketing', description: 'Gestion des offres commerciales' },
                            { id: 'users', name: 'Utilisateurs', description: 'Gestion des comptes' },
                            { id: 'apprenants', name: 'Apprenants', description: 'Gestion des apprenants' },
                            { id: 'monof', name: 'Mon OF', description: 'Paramètres de mon organisme de formation' },
                            { id: 'block-evaluation', name: 'Évaluation de Bloc', description: 'Gestion des évaluations de bloc' },
                            { id: 'organizations', name: 'Organismes', description: 'Gestion des organismes de formation' },
                            { id: 'enrollment', name: 'Inscriptions', description: 'Gestion des inscriptions aux parcours' },
                            { id: 'learnerSpace', name: 'Espace Apprenant', description: "Accès à l'interface apprenant" },
                            { id: 'reports', name: 'Rapports', description: 'Génération de rapports apprenants' },
                            { id: 'questions', name: 'Questions', description: 'Gestion des questions apprenants' },
                            { id: 'chat', name: 'Messagerie', description: 'Messagerie sécurisée E2EE' },
                            { id: 'error', name: 'Error Reporter', description: 'Journal des erreurs système' }
                        ]
                            .filter(app => {
                                // Only show apps that are authorized
                                // @ts-ignore - Dynamic access to authorizedApps
                                return user?.authorizedApps?.[app.id] ?? false;
                            })
                            .map(app => (
                                <ListItem key={app.id}>
                                    <ListItemText primary={app.name} secondary={app.description} />
                                    <ListItemSecondaryAction>
                                        <Switch
                                            checked={enabledApps[app.id as keyof typeof enabledApps] ?? false}
                                            onChange={() => handleAppToggle(app.id)}
                                        />
                                    </ListItemSecondaryAction>
                                </ListItem>
                            ))
                        }
                        {/* Show message if no apps are authorized */}
                        {Object.values(user?.authorizedApps || {}).every(val => !val) && (
                            <ListItem>
                                <ListItemText
                                    primary="Aucune application disponible"
                                    secondary="Contactez votre administrateur pour obtenir l'accès aux applications"
                                    primaryTypographyProps={{ color: 'text.secondary' }}
                                />
                            </ListItem>
                        )}
                    </List>
                    <Button
                        variant="contained"
                        onClick={saveAppSettings}
                        sx={{ ...designTokens.actionButton, mt: 3 }}
                        disabled={Object.values(user?.authorizedApps || {}).every(val => !val)}
                    >
                        Sauvegarder les applications
                    </Button>
                </TabPanel>
            </Card>
        </Box>
    );
};

export default SettingsPage;
